/**
 * Jackpot Composable
 * 管理彩金列表
 * 原始來源: cuora-module/src/machine/activity/jackpot.js
 *
 * 架構：
 * - 狀態管理：使用 Pinia store (stores/rewards/jackpot.js)
 * - 業務邏輯：保留在 composable 中
 */

import { storeToRefs } from 'pinia'
import { onBeforeUnmount, getCurrentInstance } from 'vue'

import { activityApi } from '@/api'
import { useJackpotStore } from '@/stores/rewards/jackpot'

export const useJackpot = () => {
  const nuxtApp = useNuxtApp()
  const { $request } = nuxtApp
  const { appSecret } = useRuntimeConfig().public

  const store = useJackpotStore()
  const { tick, jackpotList, jackpotEnable, loading, error } = storeToRefs(store)

  // SSR-safe: 使用 nuxtApp context 存儲 timer，避免記憶體洩漏
  if (!nuxtApp._jackpotContext) {
    nuxtApp._jackpotContext = {
      refreshTimer: null,
    }
  }
  const ctx = nuxtApp._jackpotContext

  const fetchList = async () => {
    // 如果彩金功能未啟用，不載入
    if (!jackpotEnable.value) {
      store.setJackpotList([])
      return
    }

    store.setLoading(true)
    store.setError(null)

    try {
      const response = await $request.send(activityApi.getJackpotList({ appSecret }))
      const data = response.data
      store.setJackpotList(data || [])
      return jackpotList.value
    } catch (error) {
      store.setError(error)
      store.setJackpotList([])
      console.error('[useJackpot] fetchList error:', error)
      throw error
    } finally {
      store.setLoading(false)
    }
  }

  const setJackpotEnable = async (enable) => {
    store.setJackpotEnable(enable)

    if (enable) {
      await fetchList()
      startAutoRefresh()
    } else {
      store.setJackpotList([])
      stopAutoRefresh()
    }
  }

  const startAutoRefresh = () => {
    if (!import.meta.client) return

    stopAutoRefresh()

    if (!jackpotEnable.value) {
      return
    }

    ctx.refreshTimer = setInterval(() => {
      // 背景輪詢：fetchList 內已寫入 store.error 並記錄 log，這裡吞掉 rejection 避免 unhandledrejection 噪音
      fetchList().catch(() => {})
    }, tick.value)
  }

  const stopAutoRefresh = () => {
    if (!import.meta.client) return

    if (ctx.refreshTimer) {
      clearInterval(ctx.refreshTimer)
      ctx.refreshTimer = null
    }
  }

  // 元件卸載時清理 timer，避免記憶體洩漏
  if (getCurrentInstance()) {
    onBeforeUnmount(() => {
      stopAutoRefresh()
    })
  }

  return {
    tick,
    jackpotList,
    jackpotEnable,
    loading,
    error,
    fetchList,
    setJackpotEnable,
    startAutoRefresh,
    stopAutoRefresh,
  }
}
